<template>
  <div class="chat-page">

    <!-- ── Header ─────────────────────────────────────────────── -->
    <div class="chat-header">
      <button class="back-btn" @click="$router.push('/event_chat/list')">
        <i class="fa fa-arrow-left"></i>
      </button>

      <div class="header-info">
        <div class="event-avatar">{{ eventName.charAt(0).toUpperCase() }}</div>
        <div>
          <div class="event-title">{{ eventName }}</div>
          <div class="event-sub">Event Broadcast Room</div>
        </div>
      </div>

      <div class="header-right">
        <span class="conn-badge" :class="socketConnected ? 'conn-live' : 'conn-off'">
          <span class="conn-dot"></span>
          {{ socketConnected ? 'Live' : 'Offline' }}
        </span>
        <span class="msg-count">{{ messages.length }} messages</span>
      </div>
    </div>

    <!-- ── Chat body ──────────────────────────────────────────── -->
    <div class="chat-body" ref="chatContainer">

      <!-- Empty state -->
      <div v-if="messages.length === 0 && !loading" class="empty-state">
        <div class="empty-icon">💬</div>
        <p>No messages yet.</p>
        <span>Messages broadcast here will reach all event participants.</span>
      </div>

      <!-- Messages: flat list mixes date-divider rows and bubble rows -->
      <div
        v-for="item in flatMessages"
        :key="item._key"
        :class="item._type === 'divider'
          ? 'date-divider'
          : ['msg-row', isMyMessage(item) ? 'row-me' : 'row-them']"
      >
        <!-- DATE DIVIDER -->
        <span v-if="item._type === 'divider'">{{ formatDivider(item.createdAt) }}</span>

        <!-- BUBBLE -->
        <template v-else>
          <div v-if="!isMyMessage(item)" class="avatar">
            {{ getInitials(getSenderName(item)) }}
          </div>
          <div class="bubble-wrap">
            <div class="sender-name">
              
              <span class="sender-text-name">{{ getSenderName(item) }}</span>
              <span v-if="!isMyMessage(item) && resolveRole(item) === 'admin'" class="role-chip admin-chip">Admin</span>
            </div>
            <div class="bubble" :class="isMyMessage(item) ? 'bubble-me' : 'bubble-them'">
              <span class="bubble-text">{{ item.content }}</span>
              <span class="bubble-time">{{ formatTime(item.createdAt) }}</span>
            </div>
          </div>
          <div v-if="isMyMessage(item)" class="avatar avatar-me">
            <i class="fa fa-user-shield"></i>
          </div>
        </template>
      </div>

      <!-- Loading spinner in body -->
      <div v-if="loading" class="body-loading">
        <div class="spinner"></div>
      </div>
    </div>

    <!-- ── Typing indicator ───────────────────────────────────── -->
    <div class="typing-bar" :class="{ 'typing-visible': !!typingText }">
      <div class="typing-dots">
        <span></span><span></span><span></span>
      </div>
      <span class="typing-text">{{ typingText }}</span>
    </div>

    <!-- ── Input bar ──────────────────────────────────────────── -->
    <div class="input-bar">
      <div class="input-wrap">
        <input
          ref="msgInput"
          class="msg-input"
          placeholder="Broadcast to all participants…"
          v-model="newMessage"
          @input="handleTyping"
          @keydown.enter.prevent="sendMessage"
          :disabled="!socketConnected"
        />
        <button
          class="send-btn"
          @click="sendMessage"
          :disabled="sending || !newMessage.trim() || !socketConnected"
        >
          <i class="fa fa-paper-plane"></i>
        </button>
      </div>
      <div class="input-hint" v-if="!joined">Connecting to room…</div>
    </div>

  </div>
</template>

<script>
import moment from "moment";
import { io } from "socket.io-client";

export default {
  data() {
    return {
      loading: false,
      sending: false,
      chatId: null,
      eventId: null,
      eventName: "Event Chat",
      messages: [],
      newMessage: "",
      socket: null,
      socketConnected: false,
      joined: false,
      typingUsers: {},
      myAdminId: null,
    };
  },

  computed: {
    typingText() {
      const names = Object.values(this.typingUsers).map(
        u => u.name || (u.role === 'admin' ? 'Admin' : 'Participant')
      );
      if (!names.length) return '';
      if (names.length === 1) return `${names[0]} is typing…`;
      if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`;
      return `${names[0]} and ${names.length - 1} others are typing…`;
    },

    // Flat list interleaving date-divider sentinel objects + message objects.
    // A single v-for on real <div> elements — no <template v-for> needed.
    flatMessages() {
      const items = [];
      this.messages.forEach((msg, idx) => {
        const prev = this.messages[idx - 1];
        if (idx === 0 || this.isDifferentDay(prev, msg)) {
          items.push({
            _type: 'divider',
            _key:  'd-' + idx,
            createdAt: msg.createdAt,
          });
        }
        items.push(Object.assign({}, msg, {
          _type: 'msg',
          _key:  'b-' + (msg._id || idx),
        }));
      });
      return items;
    },
  },

  async mounted() {
    this.chatId    = this.$route.query.chatId;
    this.eventId   = this.$route.query.eventId;
    this.myAdminId = window.localStorage.getItem("userid");

    if (this.chatId) {
      await this.fetchHistoryByChatId();
    } else if (this.eventId) {
      await this.fetchHistory();
    }
    if (this.eventId) {
      await this.joinRoom();
      this.initSocket();
    }
  },

  beforeDestroy() {
    if (this.socket) { this.socket.disconnect(); this.socket = null; }
  },

  methods: {
    // ── Helpers ───────────────────────────────────────────────
    isMyMessage(msg) {
      if (msg.senderRole === 'admin' ||  msg.senderRole === 'Admin') return true;
      let senderId;
      if (typeof msg.sender === 'object' && msg.sender) {
        senderId = msg.sender._id ? msg.sender._id.toString() : null;
      } else {
        senderId = msg.sender ? msg.sender.toString() : null;
      }
      return senderId === this.myAdminId;
    },

    resolveRole(msg) {
      if (msg.senderRole) return msg.senderRole;
      let senderId;
      if (typeof msg.sender === 'object' && msg.sender) {
        senderId = msg.sender._id ? msg.sender._id.toString() : null;
      } else {
        senderId = msg.sender ? msg.sender.toString() : null;
      }
      if (senderId === this.myAdminId) return 'admin';
      return 'user';
    },

    getSenderName(msg) {
      // 1. Use server-resolved name (from socket broadcast)
      if (msg.senderName && msg.senderName.trim()) return msg.senderName;
      // 2. Populated sender object (from history API)
      if (typeof msg.sender === 'object' && msg.sender) {
        const n = (msg.sender.first_name || '') + ' ' + (msg.sender.last_name || '');
        if (n.trim()) return n.trim();
        if (msg.sender.username) return msg.sender.username;
      }
      // 3. Role-based fallback
      const role = this.resolveRole(msg);
      return role === 'admin' || role === 'Admin' ? 'Admin' : 'User';
    },

    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
    },

    formatTime(d) {
      if (!d) return '';
      const now = moment();
      const m   = moment(d);
      const diffSec  = now.diff(m, 'seconds');
      const diffMin  = now.diff(m, 'minutes');
      const diffHour = now.diff(m, 'hours');
      const diffDay  = now.diff(m, 'days');

      if (diffSec < 60)  return 'Just now';
      if (diffMin < 60)  return diffMin + 'm ago';
      if (m.isSame(now, 'day'))   return m.format('h:mm A');           // Today → 2:30 PM
      if (diffDay === 1)          return 'Yesterday ' + m.format('h:mm A');
      if (diffDay < 7)            return m.format('ddd h:mm A');        // Mon 2:30 PM
      if (m.isSame(now, 'year'))  return m.format('MMM D, h:mm A');    // Jun 4, 2:30 PM
      return m.format('MMM D YYYY, h:mm A');                           // Jun 4 2024, 2:30 PM
    },

    formatDivider(d) {
      if (!d) return '';
      const now = moment();
      const m   = moment(d);
      const diffDay = now.diff(m, 'days');
      if (m.isSame(now, 'day'))        return 'Today';
      if (diffDay === 1)               return 'Yesterday';
      if (diffDay < 7)                 return m.format('dddd');         // Monday, Tuesday…
      if (m.isSame(now, 'year'))       return m.format('MMMM D');       // June 4
      return m.format('MMMM D, YYYY');                                  // June 4, 2024
    },

    isDifferentDay(prev, cur) {
      if (!prev || !cur) return false;
      return !moment(prev.createdAt).isSame(moment(cur.createdAt), 'day');
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const c = this.$refs.chatContainer;
        if (c) c.scrollTop = c.scrollHeight;
      });
    },

    // ── Data fetching ─────────────────────────────────────────
    async fetchHistory() {
      this.loading = true;
      try {
        const res  = await this.$apiService.getCall("event-chat/rooms/" + this.eventId + "/messages");
        const data = res.apidata.Data || {};
        this.messages  = data.messages || [];
        this.eventName = (data.event && data.event.event_name) || "Event Room";
        this.scrollToBottom();
      } catch (e) {
        console.error("Error fetching event history", e);
      }
      this.loading = false;
    },

    async fetchHistoryByChatId() {
      this.loading = true;
      try {
        const res  = await this.$apiService.getCall("event-chat/rooms/by-chat/" + this.chatId);
        const data = res.apidata.Data || {};
        this.messages  = data.messages || [];
        this.eventId   = data.eventId  || null;
        this.eventName = (data.event && data.event.event_name) || "Event Room";
        this.scrollToBottom();
      } catch (e) {
        console.error("Error fetching history by chatId", e);
      }
      this.loading = false;
    },

    // ── Socket ────────────────────────────────────────────────
    initSocket() {
      const baseUrl = this.$apiService.apiUrl.replace(/\/api\/?$/, '').replace(/\/$/, '');
      const token   = window.localStorage.getItem("token");

      this.socket = io(`${baseUrl}/chat`, {
        auth: { token: `Bearer ${token}` },
        reconnection: true,
        reconnectionDelay: 1000,
      });

      this.socket.on('connect', () => {
        this.socketConnected = true;
        this.socket.emit('event:open_chat', { eventId: this.eventId });
      });

      this.socket.on('disconnect', () => {
        this.socketConnected = false;
        this.joined = false;
      });

      this.socket.on('connect_error', () => {
        this.socketConnected = false;
      });

      this.socket.on('event:broadcast', ({ message }) => {
        if (message._id && this.messages.some(m => m._id && m._id.toString() === message._id.toString())) return;
        this.messages.push(message);
        this.scrollToBottom();
      });

      this.socket.on('event:typing', ({ userId, senderRole, typing }) => {
        if (typing) {
          if (this.typingUsers[userId]) clearTimeout(this.typingUsers[userId].timer);
          const timer = setTimeout(() => { this.$delete(this.typingUsers, userId); }, 3000);
          this.$set(this.typingUsers, userId, {
            name: senderRole === 'admin' ? 'Admin' : 'Participant',
            role: senderRole,
            timer,
          });
        } else {
          if (this.typingUsers[userId]) {
            clearTimeout(this.typingUsers[userId].timer);
            this.$delete(this.typingUsers, userId);
          }
        }
      });
    },

    async joinRoom() {
      try {
        await this.$apiService.postCall("event-chat/rooms/" + this.eventId + "/join", {});
        this.joined = true;
      } catch (e) {
        console.error("Error joining room", e);
      }
    },

    // ── Typing ────────────────────────────────────────────────
    handleTyping() {
      if (!this.socket || !this.socketConnected || !this.eventId) return;
      this.socket.emit('event:typing_start', { eventId: this.eventId });
      if (this._typingTimer) clearTimeout(this._typingTimer);
      this._typingTimer = setTimeout(() => {
        this.socket.emit('event:typing_stop', { eventId: this.eventId });
      }, 2000);
    },

    // ── Send ──────────────────────────────────────────────────
    async sendMessage() {
      const content = this.newMessage.trim();
      if (!content || !this.socket || !this.socketConnected) return;
      this.socket.emit('event:typing_stop', { eventId: this.eventId });
      if (this._typingTimer) { clearTimeout(this._typingTimer); this._typingTimer = null; }
      this.sending    = true;
      this.newMessage = "";
      this.socket.emit('event:send', { eventId: this.eventId, content, type: 'text' });
      this.sending = false;
      this.$nextTick(() => this.$refs.msgInput && this.$refs.msgInput.focus());
    },
  },
};
</script>

<style scoped>
/* ── Page shell ──────────────────────────────────────────────── */
.chat-page {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 180px);
  min-height: 450px;
  background: #0d1117;
  border-radius: 12px;
  overflow: hidden;
  margin: 0;
  box-shadow: 0 8px 40px rgba(0,0,0,0.6);
}

/* ── Header ──────────────────────────────────────────────────── */
.chat-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #1a1f2e 0%, #0d1117 100%);
  border-bottom: 1px solid rgba(255,255,255,0.07);
  flex-shrink: 0;
}

.back-btn {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  color: #e2e8f0;
  width: 36px; height: 36px;
  border-radius: 10px;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.2s;
  flex-shrink: 0;
}
.back-btn:hover { background: rgba(255,255,255,0.12); }

.header-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}
.event-avatar {
  width: 42px; height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, #6366f1, #a855f7);
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 18px; color: white;
  flex-shrink: 0;
}
.event-title {
  font-size: 16px;
  font-weight: 700;
  color: #f1f5f9;
  letter-spacing: 0.2px;
}
.event-sub {
  font-size: 11px;
  color: #64748b;
  margin-top: 1px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.conn-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
}
.conn-live  { background: rgba(0,242,195,0.12); color: #00f2c3; border: 1px solid rgba(0,242,195,0.3); }
.conn-off   { background: rgba(253,93,147,0.12); color: #fd5d93; border: 1px solid rgba(253,93,147,0.3); }
.conn-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: currentColor;
  animation: pulse 1.5s infinite;
}
@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }

.msg-count {
  font-size: 11px;
  color: #475569;
  background: rgba(255,255,255,0.05);
  border-radius: 8px;
  padding: 3px 9px;
}

/* ── Chat body ───────────────────────────────────────────────── */
.chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 20px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  scroll-behavior: smooth;
}
.chat-body::-webkit-scrollbar { width: 4px; }
.chat-body::-webkit-scrollbar-track { background: transparent; }
.chat-body::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }

/* Empty state */
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #475569;
  text-align: center;
  padding: 40px;
}
.empty-icon { font-size: 48px; margin-bottom: 12px; }
.empty-state p { font-size: 16px; color: #64748b; margin: 0 0 6px; }
.empty-state span { font-size: 13px; color: #334155; }

/* Date divider */
.date-divider {
  text-align: center;
  margin: 16px 0 8px;
}
.date-divider span {
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.08);
  color: #64748b;
  font-size: 11px;
  padding: 3px 12px;
  border-radius: 20px;
}

/* Message rows */
.msg-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 6px;
  animation: fadeUp 0.2s ease;
}
@keyframes fadeUp { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }

.row-me    { flex-direction: row-reverse; }
.row-them  { flex-direction: row; }

/* Avatar */
.avatar {
  width: 32px; height: 32px; border-radius: 50%;
  background: linear-gradient(135deg, #1d8cf8, #3358f4);
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700; color: white;
  flex-shrink: 0;
}
.avatar-me {
  background: linear-gradient(135deg, #e14eca, #a855f7);
  font-size: 13px;
}

/* Bubble wrap + sender */
.bubble-wrap {
  display: flex;
  flex-direction: column;
  max-width: 68%;
}
.row-me .bubble-wrap { align-items: flex-end; }

.sender-name {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  margin-bottom: 3px;
  padding-left: 4px;
  display: flex;
  align-items: center;
  gap: 5px;
}
.role-chip {
  font-size: 9px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 8px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}
.admin-chip { background: rgba(225,78,202,0.2); color: #e14eca; }

/* Bubbles */
.bubble {
  position: relative;
  padding: 10px 14px;
  border-radius: 16px;
  line-height: 1.5;
  display: flex;
  flex-direction: column;
  gap: 4px;
  word-break: break-word;
}
.bubble-me {
  background: linear-gradient(135deg, #e14eca 0%, #a855f7 100%);
  color: white;
  border-bottom-right-radius: 4px;
  box-shadow: 0 4px 16px rgba(225,78,202,0.25);
}
.bubble-them {
  background: linear-gradient(135deg, #1e293b 0%, #0f1a2e 100%);
  color: #e2e8f0;
  border: 1px solid rgba(255,255,255,0.07);
  border-bottom-left-radius: 4px;
}
.bubble-text { font-size: 14px; }
.bubble-time {
  font-size: 10px;
  opacity: 0.6;
  text-align: right;
  align-self: flex-end;
  white-space: nowrap;
}

/* Body loading */
.body-loading {
  display: flex; align-items: center; justify-content: center;
  flex: 1; padding: 40px;
}
.spinner {
  width: 32px; height: 32px;
  border: 3px solid rgba(255,255,255,0.08);
  border-top-color: #e14eca;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Typing bar ──────────────────────────────────────────────── */
.typing-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 24px;
  height: 0;
  overflow: hidden;
  opacity: 0;
  transition: height 0.2s, opacity 0.2s, padding 0.2s;
  color: #64748b;
  font-size: 12px;
  font-style: italic;
}
.typing-visible {
  height: 28px;
  opacity: 1;
  padding: 4px 24px;
}
.typing-dots {
  display: flex; gap: 3px; align-items: center;
}
.typing-dots span {
  width: 5px; height: 5px; border-radius: 50%;
  background: #64748b;
  animation: tdot 1.2s infinite ease-in-out;
}
.typing-dots span:nth-child(2) { animation-delay: .2s; }
.typing-dots span:nth-child(3) { animation-delay: .4s; }
@keyframes tdot { 0%,80%,100%{transform:scale(.6);opacity:.4} 40%{transform:scale(1);opacity:1} }

/* ── Input bar ───────────────────────────────────────────────── */
.input-bar {
  padding: 12px 16px;
  background: rgba(15,23,42,0.9);
  border-top: 1px solid rgba(255,255,255,0.06);
  flex-shrink: 0;
}
.input-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 14px;
  padding: 6px 6px 6px 16px;
  transition: border-color 0.2s;
}
.input-wrap:focus-within {
  border-color: rgba(225,78,202,0.5);
  box-shadow: 0 0 0 3px rgba(225,78,202,0.08);
}
.msg-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #e2e8f0;
  font-size: 14px;
  placeholder-color: #475569;
}
.msg-input::placeholder { color: #475569; }
.msg-input:disabled { opacity: 0.4; cursor: not-allowed; }

.send-btn {
  width: 38px; height: 38px;
  border-radius: 10px;
  background: linear-gradient(135deg, #e14eca, #a855f7);
  border: none;
  color: white;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: opacity 0.2s, transform 0.1s;
  flex-shrink: 0;
}
.send-btn:hover:not(:disabled) { opacity: 0.9; transform: scale(1.05); }
.send-btn:disabled { opacity: 0.35; cursor: not-allowed; }

.input-hint {
  font-size: 11px;
  color: #475569;
  text-align: center;
  margin-top: 6px;
}
</style>
